import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Send, UserPlus, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const input = "rounded-sm bg-navy border border-gold/30 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-gold outline-none";

export const DriversManager = () => {
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [sending, setSending] = useState(false);

  const load = () => api.get("/admin/drivers").then(({ data }) => setDrivers(data)).catch(() => toast.error("Could not load drivers."));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Enter the driver's name.");
    try {
      await api.post("/admin/drivers", { name: form.name, email: form.email || null, phone: form.phone || null });
      setForm({ name: "", email: "", phone: "" });
      toast.success("Driver added.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not add driver."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this driver?")) return;
    await api.delete(`/admin/drivers/${id}`).catch(() => {});
    load();
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/day-sheet/send");
      const who = data.drivers_emailed.length ? data.drivers_emailed.join(", ")
        : data.test_site ? "none (drivers are only emailed from your live website)"
        : data.assigned ? "none (check the driver names and emails match the bookings)" : "none (no rides with a driver today)";
      toast.success(`Today's sheet sent to you: ${data.rides} ride(s). Drivers emailed: ${who}.`);
    } catch { toast.error("Could not send day sheet."); }
    finally { setSending(false); }
  };

  return (
    <div className="rounded-sm bg-navy-light border border-gold/20 p-6 space-y-6" data-testid="drivers-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-white">Drivers & day sheet</h2>
          <p className="text-sm text-slate-400 mt-1">Every morning at 6:00 am each driver gets an email with their rides for the day. The name here must match the driver name on the booking.</p>
        </div>
        <button onClick={sendNow} disabled={sending} data-testid="send-day-sheet-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-semibold text-navy hover:bg-gold-light transition-colors duration-200 disabled:opacity-60">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send today's sheet now
        </button>
      </div>
      <form onSubmit={add} className="flex flex-wrap gap-2" data-testid="add-driver-form">
        <input className={input} placeholder="Driver name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="driver-name-input" />
        <input className={input} type="email" placeholder="Driver email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="driver-email-input" />
        <input className={input} placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="driver-phone-input" />
        <button type="submit" data-testid="add-driver-btn" className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-sm font-semibold text-gold hover:border-gold transition-colors duration-200"><UserPlus className="h-4 w-4" /> Add driver</button>
      </form>
      {drivers.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="no-drivers">No drivers yet. You will still get the full day sheet every morning.</p>
      ) : (
        <ul className="divide-y divide-gold/10">
          {drivers.map((d) => (
            <li key={d.id} className="flex items-center justify-between py-3" data-testid={`driver-row-${d.id}`}>
              <div><div className="text-white font-medium">{d.name}</div><div className="text-xs text-slate-400">{d.email || "no email"}{d.phone ? ` · ${d.phone}` : ""}</div></div>
              <button onClick={() => remove(d.id)} data-testid={`delete-driver-${d.id}`} className="text-red-300 hover:text-red-200"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
